import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";
import AdvancedTable from "@/components/table/AdvancedTable";

function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function StoragePage() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/storage`);
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load storage usage"));
    }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const onDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/review-items/${confirmDel.id}`);
      toast.success(`Deleted "${confirmDel.title}"`);
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to delete"));
    }
  };

  const rows = data?.items || [];

  // Deriving typed values so grand-total sum works cleanly on `total_bytes`.
  const columns = [
    { key: "title", label: "Title", width: 300, sortable: true, filterType: "text",
      render: (r) => <span className="font-medium truncate block">{r.title}</span>,
      exportValue: (r) => r.title },
    { key: "item_type", label: "Type", width: 140, groupable: true,
      filterType: "select",
      filterOptions: Array.from(new Set(rows.map((r) => r.item_type)))
        .filter(Boolean).map((v) => ({ value: v, label: v })),
      render: (r) => <span className="chip">{r.item_type}</span>,
      exportValue: (r) => r.item_type },
    { key: "version_count", label: "Versions", type: "number", width: 90, align: "right", sum: true,
      exportValue: (r) => r.version_count },
    { key: "total_bytes", label: "Size", type: "number", width: 120, align: "right",
      sum: true, filterType: "number-range",
      accessor: (r) => r.total_bytes || 0,
      render: (r) => <span className="font-mono text-xs">{fmtBytes(r.total_bytes || 0)}</span>,
      exportValue: (r) => r.total_bytes || 0 },
    { key: "created_at", label: "Created", width: 160, filterType: "date-range",
      render: (r) => <span className="text-xs text-muted-foreground">{r.created_at ? fmtRelative(r.created_at) : "—"}</span>,
      exportValue: (r) => r.created_at || "" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <HardDrive className="w-6 h-6 text-primary" /> Storage
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Per-item storage breakdown. Workspace owners and admins can delete to free space.
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="storage-summary">
          <Stat label="Total storage" value={fmtBytes(data.total_bytes || 0)} />
          <Stat label="Items" value={rows.length} />
          <Stat label="Screenshot cache" value={fmtBytes(data.screenshot_cache_bytes || 0)} />
        </div>
      )}

      <AdvancedTable
        tableKey="storage"
        title="Storage"
        rows={rows}
        columns={columns}
        loading={!data}
        emptyTitle="No stored items"
        emptyDescription="Upload files or attach media to review items to see them here."
        renderRowActions={(r) => (
          <Button variant="ghost" size="sm" onClick={() => setConfirmDel(r)} className="rounded-none h-7 text-destructive hover:text-destructive" data-testid={`storage-delete-${r.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      />

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent data-testid="storage-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDel?.title}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes all versions, comments, review links, invites, and cached screenshots for this item.
              You cannot undo this action.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="storage-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="storage-delete-confirm">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-border p-4">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className="text-2xl font-bold font-mono">{value}</div>
    </div>
  );
}
